import React, { useEffect, useMemo, useState } from "react";

import { Avatar, Box, Button, Checkbox, CircularProgress, Stack, TextField, Typography } from "@mui/material";
import { useActionHook } from "hooks";
import { useUserActions } from "store";

import { getInitials } from "./bookmark-page";
import { AVAILABLE_ROLES_FOR_BOOKMARK } from "./bookmark-page";

interface Props {
  initialSelectedIds: number[];
  doneLabel?: string;
  onSubmit: (selectedIds: number[]) => void;
  onCancel: () => void;
}

export const PeoplePicker: React.FC<Props> = ({ initialSelectedIds, doneLabel = "Done", onSubmit, onCancel }) => {
  const { getUsersByRoleAction } = useUserActions();
  const {
    action: fetchUsers,
    data: users = [],
    loading
  } = useActionHook((role_ids: number[]) => getUsersByRoleAction(role_ids));

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set(initialSelectedIds));

  useEffect(() => {
    fetchUsers(AVAILABLE_ROLES_FOR_BOOKMARK);
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) => {
      const name = (u.full_name || "").toLowerCase();
      return name.includes(q);
    });
  }, [users, search]);

  const toggle = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const count = selectedIds.size;

  return (
    <Stack sx={{ width: "320px", height: "400px", overflow: "hidden" }}>
      <Stack
        direction="row"
        alignItems="center"
        gap="8px"
        sx={{ padding: "8px 12px", borderBottom: "1px solid #dbe8f5" }}
      >
        <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "text.primary" }}>
          Share with specific people
        </Typography>
      </Stack>

      <Box sx={{ padding: "8px 12px", borderBottom: "1px solid #dbe8f5" }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search team members…"
          fullWidth
          autoFocus
          inputProps={{ style: { fontSize: "13px" } }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: "auto", padding: "4px 0", minHeight: 0 }}>
        {loading && (
          <Stack alignItems="center" justifyContent="center" sx={{ padding: "20px" }}>
            <CircularProgress size={20} sx={{ color: "text.secondary" }} />
          </Stack>
        )}
        {!loading && filtered.length === 0 && (
          <Typography sx={{ padding: "16px 12px", textAlign: "center", fontSize: "12px", color: "text.secondary" }}>
            No team members found.
          </Typography>
        )}
        {!loading &&
          filtered.map((user) => {
            const checked = selectedIds.has(user.id);
            return (
              <Stack
                key={user.id}
                component="label"
                direction="row"
                alignItems="center"
                gap="8px"
                sx={{
                  padding: "6px 12px",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "#ecf1f8" }
                }}
              >
                <Checkbox checked={checked} onChange={() => toggle(user.id)} sx={{ padding: 0 }} />
                <Avatar src={user.profile_photo_url || undefined} sx={{ width: 24, height: 24, fontSize: 10 }}>
                  {getInitials(user.full_name)}
                </Avatar>
                <Stack sx={{ minWidth: 0, flex: 1 }}>
                  <Typography sx={{ fontSize: "13px", color: "text.primary", lineHeight: 1.2 }} noWrap>
                    {user.full_name}
                  </Typography>
                </Stack>
              </Stack>
            );
          })}
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        gap="8px"
        sx={{ padding: "8px 12px", borderTop: "1px solid #dbe8f5" }}
      >
        <Typography sx={{ fontSize: "11px", color: "text.secondary" }}>
          {count === 0 ? "No one selected" : `${count} selected`}
        </Typography>
        <Stack direction="row" gap="4px">
          <Button onClick={onCancel} variant="text" sx={smallBtnSx}>
            Cancel
          </Button>
          <Button
            onClick={() => onSubmit(Array.from(selectedIds))}
            disabled={count === 0}
            variant="contained"
            sx={smallBtnSx}
          >
            {doneLabel}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
};

const smallBtnSx = {
  textTransform: "none",
  fontSize: "12px",
  fontWeight: 500,
  height: "28px",
  padding: "0 14px",
  borderRadius: "128px"
};
